import styles from './index.module.less';
import { App, Button } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { HairLibPublicModel, HairShareBloggerInfoModel, HairstyleBloggerLabelModel } from '@/types/hairstyle';
import HairstyleCardItemContent from '@/components/Common/HairstyleCardItemContent';
import {
  fetchHairstyleBloggerFollowSubmitData,
  fetchHairstyleBloggerInfoData,
  fetchHairstyleBloggerInfoPageData
} from '@/lib/service';
import Image from 'next/image';
import { getColorFromString, imgUrl } from '@/shared/utils';
import classNames from 'classnames';
import { CommonAlert } from '@/components/Common/CommonAlert';
import Masonry from 'react-masonry-css';
import Link from 'next/link';
import UploadImageContent from '@/components/HairChangeContent/UploadImageContent';
import ReportModal from '@/components/Common/ReportModal';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import { TextAndLinkShareAlertContent } from '@/components/Common/HairShareAlert';
import { PRODUCATION_HOST } from '@/shared/app-common';

const InfluencersDetailContent = ({ data }: { data: any }) => {
  const divRef = useRef(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const router = useRouter();
  const { locale, query } = router;
  const { name } = query;

  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const [nextPage, setNextPage] = useState(false);
  const [mainLoading, setMainLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [openAlertData, setOpenAlertData] = useState<any>(null);
  const [reportModalVisible, setReportModalVisible] = useState(false);

  const [noData, setNoData] = useState(data.initialHasNext === false);
  const [listParams, setListParams] = useState({
    page_no: 1,
    page_size: 30
  });

  const breakpointColumnsObj = {
    default: 7,
    1030: 6,
    900: 5,
    750: 4,
    576: 3,
    400: 2
  };

  const [currentInfoModel, setCurrentInfoModel] = useState<HairShareBloggerInfoModel>(
    data.initialBloggerInfo || undefined
  );
  const [selectItem, setSelectItem] = useState<HairstyleBloggerLabelModel>();
  const [pictureList, setPictureList] = useState<HairLibPublicModel[]>(data.initialHairstyleList || []);
  const [libId, setLibId] = useState<any>(null);

  const onFollowSubmitAction = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      return;
    }
    if (followLoading) {
      return;
    }
    setFollowLoading(true);
    fetchHairstyleBloggerFollowSubmitData(!currentInfoModel?.following_mark, currentInfoModel?.nickname, locale)
      .then((res) => {
        setFollowLoading(false);
        if (res?.code === 0) {
          setCurrentInfoModel({
            ...currentInfoModel,
            following_mark: !currentInfoModel?.following_mark
          });
        } else if (res?.code === 32000) {
          setOpenAlertData({
            title: `Subscribe to Follow Influencers`,
            desc: `To follow this influencer, you'll need to subscribe. By subscribing, you can try on all the hairstyles from this influencer and receive their weekly curated hairstyle Catalog.`,
            btnTitle: `Subscribe Now`,
            path: `/plan?ref=${router?.asPath}`
          });
        } else if (res?.code === 32001) {
          setOpenAlertData({
            title: `Upgrade to Follow More Influencers`,
            desc: `You've reached your follower limit. Upgrade your subscription to follow more hairstyle influencers and stay trendy!`,
            btnTitle: `Upgrade Now`,
            path: `/plan?ref=${router?.asPath}`
          });
        } else {
          message.error(res?.message);
        }
      })
      .catch((e) => {
        setFollowLoading(false);
        message.error(e?.message);
      });
  };

  const loadMainData = () => {
    if (currentInfoModel) {
      return;
    }

    fetchHairstyleBloggerInfoData(`${name}`, locale)
      .then((res) => {
        if (res?.code === 0) {
          setCurrentInfoModel(res?.data);
        } else {
          message.error(res?.message);
        }
      })
      .catch((e) => {
        message.error(e?.message);
      });
  };

  const loadMainList = (params: any, tempNoData: boolean) => {
    if (tempNoData) {
      return;
    }

    if (params.page_no === 1 && pictureList.length > 0 && !params.label && !selectItem) {
      return;
    }

    if (pictureList?.length < 30 && params?.page_no > 1) {
      return;
    }

    fetchHairstyleBloggerInfoPageData(`${name}`, params?.page_no, params?.page_size, params?.label, locale)
      .then((res) => {
        if (res?.code === 0) {
          setNoData(!res?.has_next || (res?.data?.length ?? 0) === 0);

          if (Number(params['page_no']) === 1) {
            setPictureList([...res?.data]);
          } else {
            setPictureList([...pictureList, ...res?.data]);
          }
        } else {
          loadMainListError(res?.message);
        }
        setMainLoading(false);
        setNextPage(false);
      })
      .catch((e) => {
        loadMainListError(e?.message);
        setMainLoading(false);
        setNextPage(false);
      });
  };

  const loadMainListError = (error?: any) => {
    const pageNo = Number(listParams['page_no']) - 1;
    const tempParams = { ...listParams, page_no: pageNo < 1 ? 1 : pageNo };
    setListParams(tempParams);
    message.error(error);
  };

  useEffect(() => {
    if (!data.initialBloggerInfo || !data.initialHairstyleList.length) {
      setMainLoading(false);
      setNoData(false);
      setNextPage(false);

      loadMainData();

      const tempListParams = {
        page_no: 1,
        page_size: 30
      };
      setListParams({ ...tempListParams });
      loadMainList(tempListParams, false);
    }
  }, [data]);

  useEffect(() => {
    if (nextPage) {
      const tempParams = { ...listParams, page_no: Number(listParams['page_no']) + 1 };
      setListParams(tempParams);
      loadMainList(tempParams, noData);
    }
  }, [nextPage]);

  useEffect(() => {
    const handleScroll = () => {
      const div = divRef.current;
      if (!div) return;

      const { bottom } = div.getBoundingClientRect();
      const distanceToBottom = window.innerHeight - bottom;

      if (distanceToBottom > -400) {
        if (mainLoading || noData) {
          return;
        }
        setNextPage(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles.content} ref={divRef}>
      <div className={styles.backContainer}>
        <span onClick={() => router.back()}>{`< Back`}</span>
      </div>
      <div className={styles.topContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className={styles.iconContainer}>
            <Link href={currentInfoModel?.homepage ?? ''} target="__block" onClick={(e) => e.stopPropagation()}>
              <Image
                className={styles.icon}
                src={imgUrl(currentInfoModel?.avatar, 'mid')}
                alt={currentInfoModel?.nickname}
                width={70}
                height={70}
              />
            </Link>
            <div className={styles.infoContainer}>
              <div className={styles.name}>{currentInfoModel?.nickname}</div>
              <div className={styles.follower}>{`${currentInfoModel?.follower_num} ${t('Followers')}`}</div>
              <div className={styles.hairstyle_num}>{`${currentInfoModel?.hairstyle_num} ${t('Hairstyle')}`}</div>
            </div>
          </div>

          <div style={{ display: 'flex' }}>
            {/* 关注按钮 */}
            <div
              className={classNames(styles.followContainer, currentInfoModel?.following_mark ? '' : styles.unfollow)}
              style={{
                width: 150,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4
              }}
              onClick={onFollowSubmitAction}
            >
              {currentInfoModel?.following_mark ? (
                <StarFilled style={{ fontSize: 20, color: '#fed25b' }} />
              ) : (
                <StarOutlined style={{ fontSize: 20 }} />
              )}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '14px'
                }}
              >
                {currentInfoModel?.following_mark ? t('Following') : t('Follow')}
              </span>
            </div>
            {/* 分享按钮 */}
            {currentInfoModel?.nickname && pictureList.length > 0 && (
              <Button type="primary" className={styles.iconButton} size="large" onClick={() => setOpenShare(true)}>
                <Image src="/images/influencer/icons/icon-share.webp" alt="Share" width={18} height={18} />
              </Button>
            )}
            {/* 举报按钮 */}
            <Button
              type="primary"
              className={styles.iconButton}
              icon={<Image src="/images/hairstyle/report-icon.webp" alt="report" width={20} height={20} />}
              onClick={() => setReportModalVisible(true)}
            />
          </div>
        </div>

        <p className={styles.desc}>
          {`Please follow the page to update the latest beautiful images.Thank you very much.🍀`}
        </p>

        <div className={styles.labelContainer}>
          {(currentInfoModel?.labels ?? []).map((item) => {
            return (
              <span key={item} className={styles.label} style={{ background: getColorFromString(item) }}>
                <Link href={`/influencers?type=1&label=${item}`}>{item}</Link>
              </span>
            );
          })}
        </div>

        <div className={styles.hairstyleContainer}>
          {(currentInfoModel?.hairstyle_labels ?? []).map((item, index) => {
            return (
              <div
                key={item?.image + index}
                className={classNames(styles.itemContainer, item === selectItem ? styles.active : '')}
                onClick={() => {
                  const tempItem = item === selectItem ? null : item;
                  setSelectItem(tempItem);

                  const tempListParams = {
                    page_no: 1,
                    page_size: 30,
                    label: tempItem?.label
                  };

                  setListParams({ ...tempListParams });
                  loadMainList(tempListParams, false);
                }}
              >
                <Image className={styles.icon} src={imgUrl(item?.image)} alt={item?.label} width={30} height={30} />
                <span>{item?.label ?? ''}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Masonry
        breakpointCols={breakpointColumnsObj}
        className={styles.imgList}
        columnClassName={styles.masonryGridColumn}
      >
        {(pictureList ?? []).map((item) => {
          return (
            <HairstyleCardItemContent
              key={`${item?.lib_id}`}
              item={item}
              isDetail={true}
              onTryOn={(id) => {
                if (isLogin) {
                  setLibId(id);
                } else {
                  onOpenLoginModal(true);
                  onChangedLoginView(true);
                }
              }}
            />
          );
        })}
      </Masonry>

      <CommonAlert
        open={openAlertData !== null}
        title={openAlertData?.title}
        desc={openAlertData?.desc}
        btnTitle={openAlertData?.btnTitle}
        onCloseAction={() => setOpenAlertData(null)}
        onConfirmClick={() => {
          if (openAlertData?.path) {
            router.push(openAlertData?.path);
          }
          setOpenAlertData(null);
        }}
      />

      <UploadImageContent openId={libId} handleClose={() => setLibId(null)} />

      <ReportModal
        visible={reportModalVisible}
        onClose={() => setReportModalVisible(false)}
        nickname={currentInfoModel?.nickname}
      />

      <TextAndLinkShareAlertContent
        openType={openShare}
        yourImage={pictureList[0]?.image_url}
        yourLink={`${PRODUCATION_HOST}/influencers/${currentInfoModel?.nickname}`}
        dataList={[]}
        handleCancel={() => setOpenShare(false)}
      />
    </div>
  );
};

export default InfluencersDetailContent;
